/**
 * toSheetDraftGrid — map parsed Wesley Chapel sheet rows into the existing
 * AI-import draft pipeline, then apply the "skip unknown" rule.
 *
 * The back half of the import pipeline is source-agnostic: once a row is in
 * AssignmentRow shape, matchShortCodes → toDraftGrid produce the same draft the
 * PDF importer's review/Apply UI already consumes. This module is the thin
 * Sheet-specific adapter:
 *
 *   1. parsed sheet rows → AssignmentRow-shaped rows (room, startTime military,
 *      attendingShortCode, callOrder, isAIC, staffing[{name, role, timeNumber}])
 *   2. matchShortCodes(rows, doctors)  — deterministic attending code → id
 *   3. toDraftGrid(matched, {crnas, doctors}) — Phase-4 draft rows + flags
 *   4. MERGE the two fields the schema/PDF path doesn't carry: endTime (sheets
 *      print an explicit end) and surgeon (resolved from the Col C code).
 *   5. PARTITION into kept vs skipped. A row is skipped only when its OR name
 *      matches no live room (or is TBD). An attending code that is present but
 *      unmatched is NO LONGER a skip: the row is KEPT with the attending left
 *      unresolved and an `attending` flag, and the code is collected into
 *      newStaff.doctorCodes so the AIC can add the doctor at Apply time (which
 *      then resolves the row on a re-run). Likewise an unmatched CRNA name
 *      (ahpName set, ahpId null) is collected into newStaff.crnas. Surgeon code
 *      that doesn't resolve is a FLAG, not a skip (surgeon is a free-text field).
 *
 * Pure function — no Firebase, no network. Imports only domain/pure helpers.
 */

import { matchShortCodes } from "../ai-import/matchShortCodes";
import { toDraftGrid } from "../ai-import/toDraftGrid";
import { roomForOrRoom } from "../../domain/gridTemplate";
import { isTbdRoomName } from "../../domain/locations";
import { SECOND_PROVIDER_ROLES } from "../../domain/secondProvider";
import { resolveIdentity } from "../../domain/resolveIdentity";

const normCode = (c) => String(c ?? "").trim().toUpperCase();
// Letters-only lowercase name key for matching a bench CRNA to a pool member.
const normName = (n) => String(n ?? "").toLowerCase().replace(/[^a-z]/g, "");

/**
 * Resolve a surgeon short code to a display name against a surgeon list.
 * Surgeons may live in the same provider pool (shortCode field) or a dedicated
 * list; the caller injects whatever it has. Exact, case-insensitive on code.
 *
 * @returns {{ name: string|null, matched: boolean }}
 */
export function resolveSurgeon(code, surgeons = []) {
  const c = normCode(code);
  if (!c) return { name: null, matched: false };
  const hit = surgeons.find((s) => normCode(s.shortCode || s.code) === c);
  return { name: hit ? hit.name || hit.fullName || null : null, matched: Boolean(hit) };
}

/** parsed sheet row → AssignmentRow-shaped object (the pipeline's input shape). */
function toAssignmentRow(p) {
  return {
    room: p.room,
    startTime: p.startMilitary, // military digits or null — toDraftGrid pads to HH:MM
    attendingShortCode: p.attendingShortCode,
    callOrder: p.callOrder,
    isAIC: Boolean(p.isAIC),
    staffing: p.crnaName
      ? [{
          name: p.crnaName,
          role: "CRNA",
          timeNumber: p.crnaShiftNumber ?? null,
          // ⚠️ THE RAW CODE, not just its flattened hour. "7p-7a" as `7` reads
          // back as 19:00 — twelve hours early for the seat holder, while the
          // second provider beside them already got an honest code.
          timeCode: p.crnaShiftCode ?? null,
        }]
      : [],
  };
}

/**
 * Build the review draft from parsed Wesley Chapel rows.
 *
 * @param {Array<object>} parsedRows  — output of parsePrelimAssignments().rows
 * @param {object} ctx
 *   @param {Array<{id,shortCode,name}>} ctx.doctors  — today's rostered doctors
 *   @param {Array<{id,name}>}          ctx.crnas     — today's rostered CRNAs
 *   @param {Array<{shortCode,name}>}   ctx.surgeons  — surgeon lookup list
 *   @param {Array<{id,name}>}          ctx.rooms     — live room docs (for OR matching)
 * @returns {{ rows, skipped, gridFlags, newStaff }}
 *   rows    — kept draft rows (saveRow shape + endTime, surgeon, callOrder, isAIC, flags)
 *   skipped — [{ room, sheetRow, reason }] reason ∈ tbdRoom | unknownRoom
 *   gridFlags — passthrough grid-level flags from toDraftGrid (e.g. aicTag)
 *   newStaff — { crnas: string[], doctorCodes: string[] } — DISTINCT unmatched
 *              CRNA names and attending codes found in the KEPT rows, so the AIC
 *              can add them as staff and re-resolve the draft at Apply time.
 */
export function toSheetDraftGrid(parsedRows, { doctors = [], crnas = [], surgeons = [], rooms = [], identity = {}, sheetValues = null } = {}) {
  const assignmentRows = parsedRows.map(toAssignmentRow);
  const matched = matchShortCodes(assignmentRows, doctors);
  const { rows: draftRows, gridFlags } = toDraftGrid(matched, { crnas, doctors });

  // ⚠️ ONE DECISION ABOUT WHO A NAME IS, MADE ONCE, FOR EVERY PROVIDER ON THE
  // ROW. The seat holder used to be matched by `toDraftGrid`'s exact-string
  // equality, the second provider by a letters-only compare in this file, and
  // the bench by a third copy of that compare — three vocabularies that could
  // disagree about the same person in the same import, and did: `Taylor
  // Prentice` resolved in `WC CS` and was lost in `WC OB` on the same run.
  // `resolveIdentity` is now asked about all three, so a person is either
  // identified everywhere or asked about once.
  const resolveOne = (rawName) => resolveIdentity(rawName, {
    staff: crnas,
    aliases: identity.aliases || [],
    qgenda: identity.qgenda || null,
    facilityId: identity.facilityId || null,
  });

  /**
   * The cell as the department wrote it, for a review item.
   *
   * ⚠️ A RECONSTRUCTED NAME IS NOT EVIDENCE. A reviewer settling "who is
   * `Krista Sciulla`?" is reading the sheet over somebody's shoulder, and the
   * annotation the parser stripped ("(REQ)", "Orient to OB floor", a second
   * name after a slash) is often the whole explanation. `parsedRows` carry the
   * 1-based sheet row, so the original cell is one lookup away when the caller
   * threads the grid; without it we degrade to the parsed name rather than
   * printing something that looks verbatim and is not.
   */
  const verbatimOf = (parsed, name) => {
    if (parsed && parsed.crnaRaw) return String(parsed.crnaRaw).trim();
    const row = Array.isArray(sheetValues) ? sheetValues[(parsed?.sheetRow ?? 0) - 1] : null;
    if (Array.isArray(row) && name) {
      const cell = row.find((c) => typeof c === "string" && c.includes(name));
      if (cell) return String(cell).trim();
    }
    return name;
  };

  const kept = [];
  const skipped = [];
  // Every person the sheet named that nobody could bind to a record. ⚠️ THIS IS
  // NOT A LIST OF PEOPLE TO CREATE. It is a list of questions; the answers are
  // clicks, and a click is what writes an alias or (deliberately) a person.
  const unresolvedPeople = [];
  let placed = 0;
  const askAbout = (entry) => {
    // One question per (person, room) — the same misspelling in two rooms is two
    // rows to fix, and a reviewer confirming once fixes both on the next parse.
    const key = `${entry.room}|${entry.sheetRow}|${normName(entry.name)}`;
    if (unresolvedPeople.some((u) => `${u.room}|${u.sheetRow}|${normName(u.name)}` === key)) return;
    unresolvedPeople.push(entry);
  };
  // CRNAs the sheet parks on a TBD/placeholder room → the unassigned bench
  // (e.g. "WC TBD · Jim Slone 3"). They're real staff for the day but not yet
  // tied to a room, so they surface as unassigned, not a room card.
  const unassigned = [];
  // Attending short codes the sheet used that no doctor record carries. Kept as
  // a REPORT for the review queue; nothing creates a placeholder doctor from it.
  const newDoctorCodes = [];

  draftRows.forEach((draft, i) => {
    const parsed = parsedRows[i];
    const matchRow = matched[i];

    // Everyone the sheet named in this cell, for the two paths that cannot
    // give them a room. ⚠️ A ROW THAT CANNOT BE PLACED STILL NAMED PEOPLE.
    const benchOf = () =>
      (parsed.crnas && parsed.crnas.length)
        ? parsed.crnas
        : (draft.ahpName
            ? [{ name: draft.ahpName, shiftNumber: parsed.crnaShiftNumber ?? null, shiftCode: parsed.crnaShiftCode ?? null }]
            : []);
    const toBench = (people, reason) => {
      for (const b of people) {
        const hit = resolveOne(b.name);
        const shiftCode = b.shiftCode ?? (b.shiftNumber != null ? String(b.shiftNumber) : null);
        unassigned.push({
          // The ROSTER's spelling once we know who this is, so the bench, the
          // break board and the room card all print the same person.
          name: hit.status === "resolved" ? hit.name : b.name,
          sheetName: b.name,
          ahpId: hit.status === "resolved" ? hit.staffId : null,
          identityHow: hit.status === "resolved" ? hit.how : null,
          shiftNumber: b.shiftNumber ?? null,
          // The RAW code, so a bench CRNA's departure is not flattened either.
          shiftCode,
          sheetRow: parsed.sheetRow,
          reason,
        });
        if (hit.status === "resolved") placed += 1;
        else if (!b.issue) {
          askAbout({
            name: b.name, verbatim: verbatimOf(parsed, b.name), room: parsed.room || "",
            sheetRow: parsed.sheetRow, role: "bench", shiftCode,
            reason: hit.reason, candidates: hit.candidates || [],
          });
        }
      }
    };

    // Skip 0: TBD is an internal placeholder — never assign a provider to it.
    // EVERY CRNA the sheet parks there (a cell may list several, e.g. "Jim Slone
    // 3, Gabi Swanson 7") goes to the UNASSIGNED bench (not skipped), so they
    // still appear in Daily Assignments / the roster.
    if (isTbdRoomName(parsed.room)) {
      const bench = benchOf();
      if (bench.length) toBench(bench, "tbdRoom");
      else skipped.push({ room: parsed.room, sheetRow: parsed.sheetRow, reason: "tbdRoom" });
      return;
    }
    // Skip 1: OR name matches no live room.
    //
    // ⚠️ THE ROOM IS UNKNOWN. THE PEOPLE ARE NOT. On 2026-08-20 the sheet put
    // Sarabel Mercado Torres in ACUTECARE — a real Tampa room the importer's
    // regex claims and the department's own `Room Defs` tab does not list — and
    // this branch dropped the whole row. She was on the sheet and nowhere in
    // the output, and the skip line named the ROOM, not her. A room we cannot
    // place is a reason to bench somebody, never a reason to lose them.
    if (!roomForOrRoom(parsed.room, rooms)) {
      const bench = benchOf();
      if (bench.length) toBench(bench, "unknownRoom");
      skipped.push({
        room: parsed.room,
        sheetRow: parsed.sheetRow,
        reason: "unknownRoom",
        people: bench.map((b) => b.name),
      });
      return;
    }

    // Merge the sheet-only fields the schema/PDF path leaves blank.
    const surgeon = resolveSurgeon(parsed.surgeonCode, surgeons);
    const flags = [...(draft.flags || [])];
    const rowReview = [];
    if (parsed.surgeonCode && !surgeon.matched) {
      flags.push({ field: "surgeon", reason: "unmatchedSurgeonCode" });
    }

    // Attending code present but unresolved (provider code not known). KEEP the
    // row (attending stays null — never a guessed id), flag it, and record the
    // code so adding the doctor at Apply time resolves it on a re-run.
    if (parsed.attendingShortCode && !matchRow.attendingMatched) {
      const code = String(parsed.attendingShortCode).trim();
      if (code && !newDoctorCodes.includes(code)) newDoctorCodes.push(code);
      flags.push({ field: "attending", reason: "unknownAttendingCode" });
    }

    // —— WHO HOLDS THIS ROOM ——
    // The exact-string match inside `toDraftGrid` answers for the PDF path; the
    // sheet writes `Dom Pearson`, `Liz Lucaj`, `Chip Compton`, and the ladder is
    // what turns those into the roster's Dominique, Elizabeth and Kang.
    let seatId = draft.ahpId;
    let seatName = draft.ahpName;
    let seatHow = seatId ? "exact" : null;
    if (draft.ahpName) {
      const crnaName = String(draft.ahpName).trim();
      const hit = resolveOne(crnaName);
      if (hit.status === "resolved") {
        seatId = hit.staffId;
        seatName = hit.name;
        seatHow = hit.how;
        placed += 1;
      } else if (parsed.crnaNameIssue) {
        // ⚠️ A NAME THE READER COULD NOT VOUCH FOR IS NOT EVEN A QUESTION.
        // The row still shows it — a blank where a name belongs is worse — but
        // it must not reach a queue whose second button creates a colleague.
        rowReview.push({
          code: "nameNotOfferedForCreation",
          text: crnaName,
          detail: parsed.crnaNameIssue.code,
          room: parsed.room,
          sheetRow: parsed.sheetRow,
        });
      } else if (crnaName) {
        askAbout({
          name: crnaName, verbatim: verbatimOf(parsed, crnaName), room: parsed.room || "",
          sheetRow: parsed.sheetRow, role: "seat", shiftCode: draft.ahpShiftCode ?? null,
          reason: hit.reason, candidates: hit.candidates || [],
        });
      }
    }

    // ⚠️ TWO PEOPLE IN ONE ROOM. "Josh Larson 5 Orient Sam Gongora" names a
    // preceptor and an orientee who are BOTH in that room. The preceptor holds
    // the seat (draft.ahpName/ahpId); the orientee rides alongside so the board
    // can show them, count them as present, and give them breaks — instead of
    // listing them as free somewhere else. An orientee the pool does not know
    // is collected like any other new CRNA, so Apply adds them to the roster.
    //
    // ⚠️ "ORIENT" IS NOT THE ONLY WAY THE SHEET PUTS TWO PEOPLE IN A ROOM. It
    // also just lists them: "Jim Slone 3, Gabi Swanson 7". `parseCrnaCell`
    // returns the FIRST as the seat and the second was carried nowhere — no
    // assignment field, no roster entry, no break row, no error. That is the
    // second shape behind "these people are on the timeline and on no roster",
    // and it is the shape a person would never spot in a diff because the row
    // it produces looks complete.
    //
    // The relationship differs, so the label does: the sheet claims no teaching
    // here, only two colleagues in one room.
    const listed = Array.isArray(parsed.crnas) ? parsed.crnas : [];
    const second = parsed.crnaOrientee
      ? { name: String(parsed.crnaOrientee).trim(), shift: parsed.crnaOrienteeShift ?? null, shiftInherited: Boolean(parsed.crnaOrienteeShiftInherited), role: SECOND_PROVIDER_ROLES.ORIENTING }
      : listed.length > 1
        // A colleague listed in their own right printed their own code or none.
        // Nothing was borrowed for them.
        ? { name: String(listed[1].name || "").trim(), shift: listed[1].shiftCode ?? listed[1].shiftNumber ?? null, shiftInherited: false, role: SECOND_PROVIDER_ROLES.HELPING }
        : { name: "", shift: null, shiftInherited: false, role: SECOND_PROVIDER_ROLES.ORIENTING };

    const sheetSecondName = second.name;
    const secondHit = sheetSecondName ? resolveOne(sheetSecondName) : null;
    const orienteeResolved = secondHit && secondHit.status === "resolved";
    const orienteeName = orienteeResolved ? secondHit.name : sheetSecondName;
    const secondIssue = parsed.crnaOrientee ? parsed.crnaOrienteeIssue : (listed[1] && listed[1].issue) || null;
    if (orienteeResolved) {
      placed += 1;
    } else if (sheetSecondName && secondIssue) {
      rowReview.push({
        code: "nameNotOfferedForCreation",
        text: sheetSecondName,
        detail: secondIssue.code,
        room: parsed.room,
        sheetRow: parsed.sheetRow,
      });
    } else if (sheetSecondName) {
      askAbout({
        name: sheetSecondName, verbatim: verbatimOf(parsed, sheetSecondName), room: parsed.room || "",
        sheetRow: parsed.sheetRow, role: "second", shiftCode: second.shift != null ? String(second.shift) : null,
        reason: secondHit.reason, candidates: secondHit.candidates || [],
      });
    }

    kept.push({
      ...draft,
      ahpId: seatId,
      ahpName: seatName,
      identityHow: seatHow,
      orienteeName,
      orienteeId: orienteeResolved ? secondHit.staffId : null,
      // WHY they are in the room — the only thing that differs between an
      // orientee and a colleague helping out. The PERSON lives in the same
      // fields either way (domain/secondProvider).
      secondProviderRole: orienteeName ? second.role : null,
      // Their own leaving time, when the sheet gave one. Kept as a STRING like
      // every other shift code so `shiftCodeEndHour24` reads it directly.
      orienteeShiftCode: second.shift != null ? String(second.shift) : null,
      orienteeShiftInherited: Boolean(second.shiftInherited),
      endTime: parsed.endHHMM || "",
      surgeon: surgeon.name || parsed.surgeonCode || "",
      source: "sheet-import",
      // On-call (timeless CRNA in a real room, e.g. WC OB · Grace Elaro 24):
      // no case time; the board/roster render it as 24h / full-shift coverage.
      onCall: Boolean(parsed.onCall),
      // Sheet routing hints ("to CT" / "from IR" / "3 TOR16") — seed Next Room
      // links in linkProviderChains before its time-based pass.
      toRoomHint: parsed.crnaToRoom || "",
      fromRoomHint: parsed.crnaFromRoom || "",
      // What the sheet said besides names — column G's "First out 1130", the
      // "(REQ)" that was peeled off a name, the "Orient to OB floor" tail.
      // Read by nothing before this; discarded silently, four a day.
      sheetNotes: parsed.notes || [],
      review: rowReview,
      flags,
    });
  });

  // Every cell the reader could not fully understand, verbatim, plus every row
  // that named people but could not be placed. ⚠️ THE UNATTENDED JOB MUST BE
  // ABLE TO SHOW THIS: it full-resets each board, so a refusal nobody sees
  // rebuilds the day without that person.
  const review = [
    ...parsedRows.flatMap((p) => p.review || []),
    ...kept.flatMap((r) => r.review || []),
    ...skipped
      .filter((s) => s.reason === "unknownRoom" && (s.people || []).length)
      .map((s) => ({
        code: "unknownRoom",
        text: s.room,
        detail: `benched: ${(s.people || []).join(", ")}`,
        room: s.room,
        sheetRow: s.sheetRow,
      })),
  ];

  return {
    rows: kept,
    skipped,
    unassigned,
    gridFlags,
    review,
    // How many people-instances this draft could bind to a record. The other
    // half of the coverage report is `unresolved.people.length`; the two always
    // sum to what the sheet named, which is what makes the line checkable.
    placed,
    // ⚠️ THE FIELD THAT USED TO BE `newStaff` — AND THE RENAME IS THE POINT.
    // `newStaff.crnas` was a list of strings the unattended job passed straight
    // to `staff.add()`; on 2026-08-20 it held 22 names, and the three it did
    // refuse were the only sign that five real clinicians had been lost. These
    // are QUESTIONS. Nothing here may be created without a person clicking, and
    // no unattended path reads it at all.
    unresolved: { people: unresolvedPeople, doctorCodes: newDoctorCodes },
  };
}
